import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Divider
} from '@mui/material';
import moment from 'moment';

// आकडा शब्दात बदलण्यासाठी utility (मराठी)
// Utility: Convert number to Marathi words
const convertNumberToMarathiWords = (num) => {
  if (!num) return "";

  const ones = ["", "एक", "दोन", "तीन", "चार", "पाच", "सहा", "सात", "आठ", "नऊ", "दहा",
    "अकरा", "बारा", "तेरा", "चौदा", "पंधरा", "सोळा", "सतरा", "अठरा", "एकोणीस"];
  const tens = ["", "", "वीस", "तीस", "चाळीस", "पन्नास", "साठ", "सत्तर", "ऐंशी", "नव्वद"];

  const numStr = parseInt(num, 10).toString();

  // Helper for 2-digit numbers
  const twoDigitToWords = (n) => {
    if (n < 20) return ones[n];
    const t = Math.floor(n / 10);
    const o = n % 10;
    return tens[t] + (o ? " " + ones[o] : "");
  };

  let result = "";
  let n = parseInt(num, 10);

  if (n >= 10000000) {
    result += convertNumberToMarathiWords(Math.floor(n / 10000000)) + " कोटी ";
    n %= 10000000;
  }
  if (n >= 100000) {
    result += convertNumberToMarathiWords(Math.floor(n / 100000)) + " लाख ";
    n %= 100000;
  }
  if (n >= 1000) {
    result += convertNumberToMarathiWords(Math.floor(n / 1000)) + " हजार ";
    n %= 1000;
  }
  if (n >= 100) {
    result += convertNumberToMarathiWords(Math.floor(n / 100)) + "शे ";
    n %= 100;
  }
  if (n > 0) {
    result += twoDigitToWords(n) + " ";
  }

  return result.trim() + " फक्त";
};


export default function PrintInvoice() {
  const { saleId } = useParams();
  const [sale, setSale] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const shopDetails = {
    logo: '',
    name: "शिवअंबा पूजा साहित्य व प्रासादिक भांडार",
    address: "तुळजापूर, महाराष्ट्र, भारत",
    mobile: "8888053939",
    email: "contact@shivambamanager.com"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const saleRef = doc(db, "sales", saleId);
        const saleSnap = await getDoc(saleRef);
        if (!saleSnap.exists()) {
          setIsLoading(false);
          return;
        }
        const saleData = saleSnap.data();
        setSale({ id: saleSnap.id, ...saleData });

        // fetch customer
        const custRef = doc(db, "customers", saleData.customerId);
        const custSnap = await getDoc(custRef);
        if (custSnap.exists()) {
          setCustomer(custSnap.data());
        }

        // fetch sale items
        const itemsQuery = query(
          collection(db, "salesitems"),
          where("saleid", "==", saleId)
        );
        const itemsSnap = await getDocs(itemsQuery);
        const fetchedItems = itemsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setItems(fetchedItems);

        // collect ids for products
        const productIds = fetchedItems.map((item) => item.itemId);

        // fetch products (with size inside product doc)
        const prodMap = {};
        if (productIds.length > 0) {
          const productsQuery = query(
            collection(db, "products"),
            where("__name__", "in", productIds)
          );
          const productsSnap = await getDocs(productsQuery);
          productsSnap.docs.forEach((d) => {
            prodMap[d.id] = {
              name: d.data().productname,
              size: d.data().size || ""   // size is inside product doc
            };
          });
        }
        setProducts(prodMap);

      } catch (error) {
        console.error("Error fetching invoice data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [saleId]);

  useEffect(() => {
    if (!isLoading && sale && customer) {
      window.print();
    }
  }, [isLoading, sale, customer]);

  if (isLoading) return <Typography>बिल लोड होत आहे...</Typography>;
  if (!sale || !customer) return <Typography>बिल सापडले नाही.</Typography>;

  return (
    <Box sx={{
      maxWidth: "800px",
      margin: "auto",
      padding: "20px",
      border: "1px solid #ccc",
      borderRadius: "10px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
      fontFamily: "Noto Sans Devanagari, sans-serif"
    }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">{shopDetails.name}</Typography>
        <Typography>{shopDetails.address}</Typography>
        <Typography>मोबाईल: {shopDetails.mobile}</Typography>
        <Divider sx={{ my: 1 }} />
      </Box>

      <Typography variant="h6" fontWeight="bold" align="center" sx={{ mb: 2, color: "#1976d2" }}>
        बिल
      </Typography>

      {/* Invoice Details */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        mb: 2,
        fontSize: "14px"
      }}>
        <Box>
          <Typography>बिल क्रमांक: <strong>{sale.SaleNo || sale.id}</strong></Typography>
          <Typography>दिनांक: <strong>{moment(sale.SaleDate).format('DD/MM/YYYY')}</strong></Typography>
          <Typography>पेमेंट टाईप : <strong>{sale.paytype}</strong></Typography>
        </Box>
        <Box>
          <Typography>ग्राहक: <strong>{customer.name}</strong></Typography>
          {customer.mobile && <Typography>मोबाईल: <strong>{customer.mobile}</strong></Typography>}
          {customer.address && <Typography>पत्ता: <strong>{customer.address}</strong></Typography>}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small" sx={{ border: "1px solid #ddd" }}>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>क्र.</TableCell>
              <TableCell>तपशील</TableCell>
              <TableCell style={{ textAlign: "right" }}>दर</TableCell>
              <TableCell style={{ textAlign: "right" }}>प्रमाण</TableCell>
              <TableCell style={{ textAlign: "right" }}>डिस्काउंट</TableCell>
              <TableCell style={{ textAlign: "right" }}>एकूण</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => {
              const prod = products[item.itemId] || {};
              return (
                <TableRow key={item.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {prod.name || item.itemName || "N/A"}
                   
                  </TableCell>
                  <TableCell style={{ textAlign: "right" }}>{Number(item.rate)}</TableCell>
                  <TableCell style={{ textAlign: "right" }}>{Number(item.qty)}  {prod.size ? ` (${prod.size})` : ""}</TableCell>
                  <TableCell style={{ textAlign: "right" }}>{Number(item.discount)}</TableCell>
                  <TableCell style={{ textAlign: "right" }}>{Number(item.totalAmount)}</TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell colSpan={5} align="right" sx={{ fontWeight: "bold" }}>एकूण रक्कम</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{Number(sale.grandTotal)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="right">भरलेली रक्कम</TableCell>
              <TableCell align="right">{Number(sale.paidAmount)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="right">उर्वरित रक्कम</TableCell>
              <TableCell align="right">  {Number(sale.remaining)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Amount in Words */}
      {/* <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ fontStyle: "italic" }}>
           **रक्कम शब्दात:** {convertNumberToMarathiWords(sale.grandTotal)}
        </Typography>
      </Box> */}

      {/* Signatures */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        mt: 10,
        fontSize: "14px"
      }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography>ग्राहकाची सही</Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography>स्वीकृती सही</Typography>
          <Divider sx={{ mt: 1 }} />
        </Box>
      </Box>
    </Box>
  );
}
